<script lang="ts">
  // Push-to-talk: hold to talk; double-tap (or the latch switch) to latch on;
  // tap again to release a latch. Spacebar works as PTT on desktop.
  let {
    onchange,
    disabled = false,
    active = false,
    latched = $bindable(false),
    label = 'TALK',
    sublabel = '',
    keyboard = true,
    size = 'large',
  }: {
    onchange: (active: boolean) => void;
    disabled?: boolean;
    active?: boolean;
    latched?: boolean;
    label?: string;
    sublabel?: string;
    keyboard?: boolean;
    size?: 'large' | 'small';
  } = $props();

  let downAt = 0;
  let lastShortUp = 0;
  let ignoreUp = false;
  let holding = false;

  function down() {
    if (disabled) return;
    const t = performance.now();
    if (latched) {
      latched = false;
      onchange(false);
      ignoreUp = true;
      return;
    }
    if (t - lastShortUp < 350) {
      latched = true;
      ignoreUp = true;
      onchange(true);
      return;
    }
    holding = true;
    downAt = t;
    onchange(true);
  }

  function up() {
    if (ignoreUp) {
      ignoreUp = false;
      return;
    }
    if (!holding) return;
    holding = false;
    const t = performance.now();
    if (!latched) onchange(false);
    lastShortUp = t - downAt < 250 ? t : 0;
  }

  $effect(() => {
    if (!keyboard) return;
    const isField = (e: KeyboardEvent) => e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement;
    const kd = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || isField(e)) return;
      e.preventDefault();
      down();
    };
    const ku = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isField(e)) return;
      e.preventDefault();
      up();
    };
    // Releasing when the window loses focus avoids a stuck-open mic.
    const blur = () => {
      if (holding) up();
    };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
      window.removeEventListener('blur', blur);
    };
  });

  $effect(() => {
    if (disabled && (holding || latched)) {
      holding = false;
      latched = false;
      onchange(false);
    }
  });
</script>

<button
  class="ptt {size}"
  class:on={active}
  class:latched
  {disabled}
  aria-pressed={active}
  onpointerdown={(e) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
    down();
  }}
  onpointerup={up}
  onpointercancel={up}
  oncontextmenu={(e) => e.preventDefault()}
>
  <span class="l">{active ? (latched ? 'LATCHED' : 'TALKING') : label}</span>
  {#if sublabel}<span class="s">{sublabel}</span>{/if}
</button>

<style>
  .ptt {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
    border-radius: 50%; border: 4px solid #555; background: #222; color: #fff; font-weight: 900;
    touch-action: none; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.6);
  }
  .large { width: 128px; height: 128px; font-size: 1.25rem; }
  .small { width: 76px; height: 76px; font-size: 0.85rem; border-width: 3px; }
  .on { background: var(--talk); border-color: #fff; color: #000; transform: scale(1.04); }
  .latched { border-style: dashed; }
  .s { font-size: 0.7rem; font-weight: 600; opacity: 0.85; max-width: 90%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
